'use client'

import { useState, useEffect } from 'react'
import { Bell, Check } from 'lucide-react'
import { panel, panelInner, headingClass, mutedClass } from './ui'

interface Notification {
  id: string
  title: string
  message: string
  isRead: boolean
  notificationType: string | null
  createdAt: string
  brief: {
    id: string
    objective: string | null
  } | null
}

interface NotificationsPanelProps {
  userId: string
  onNotificationRead?: () => void
}

export default function NotificationsPanel({ userId, onNotificationRead }: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)

  useEffect(() => {
    loadNotifications()
  }, [userId, showUnreadOnly])

  const loadNotifications = async () => {
    try {
      const response = await fetch(`/api/brief613/notifications?unread_only=${showUnreadOnly}`, {
        headers: {
          'x-user-id': userId,
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON')
      }

      const data = await response.json()

      if (data.success) {
        setNotifications(data.notifications)
      } else {
        console.error('Error loading notifications:', data.error)
        setNotifications([])
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
      setNotifications([])
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await fetch('/api/brief613/notifications', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
        },
        body: JSON.stringify({ notification_id: notificationId }),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON')
      }

      const data = await response.json()

      if (data.success) {
        loadNotifications()
        // Notify parent component to update badge count
        if (onNotificationRead) {
          onNotificationRead()
        }
      } else {
        console.error('Error marking notification as read:', data.error)
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const unreadCount = notifications.filter((n) => !n.isRead).length

  if (loading) {
    return (
      <div className={`${panel} p-8 text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className={mutedClass}>Loading notifications...</p>
      </div>
    )
  }

  return (
    <div className={`${panel} p-6`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={headingClass}>
          Notifications {unreadCount > 0 && <span className="text-xs font-normal text-white/45">({unreadCount} unread)</span>}
        </h3>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showUnreadOnly}
            onChange={(e) => setShowUnreadOnly(e.target.checked)}
            className="rounded"
          />
          <span className="text-white/55 text-xs">Show unread only</span>
        </label>
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="mx-auto mb-4 text-white/30" size={32} />
          <p className={mutedClass}>No notifications</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`${panelInner} p-4 border-l-2 ${
                !notification.isRead ? 'border-l-white/40' : 'border-l-white/10'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`text-sm font-medium ${!notification.isRead ? 'text-white/90' : 'text-white/60'}`}>
                      {notification.title}
                    </h4>
                    {!notification.isRead && (
                      <span className="w-1.5 h-1.5 rounded-full bg-white/50"></span>
                    )}
                  </div>
                  <p className="text-xs text-white/55 mb-2">{notification.message}</p>
                  {notification.brief && (
                    <p className="text-xs text-white/40">Brief: {notification.brief.objective || 'N/A'}</p>
                  )}
                  <p className="text-xs text-white/40 mt-2">
                    {new Date(notification.createdAt).toLocaleString()}
                  </p>
                </div>
                {!notification.isRead && (
                  <button
                    onClick={() => markAsRead(notification.id)}
                    className="text-white/45 hover:text-white/85 transition-colors ml-4"
                    title="Mark as read"
                  >
                    <Check size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
